import { Mail, FileSpreadsheet, FolderOpen, Users, ArrowRight, Zap, CheckCircle } from 'lucide-react';

const CollaborationChaos = () => {
  const chaosItems = [
    { icon: Mail, label: '200-500 emails per acquisition', color: 'red' },
    { icon: FileSpreadsheet, label: 'Version control nightmares', color: 'orange' },
    { icon: FolderOpen, label: '5+ disconnected systems', color: 'amber' },
    { icon: Users, label: '40-60% time waiting on others', color: 'yellow' }
  ];

  const orderItems = [
    'Everyone sees the same data in real-time',
    'AI drafts update as requirements change',
    'Comments and approvals in one place',
    'Zero "who has the latest version?" emails'
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            From{' '}
            <span className="text-red-600">Chaos</span>{' '}
            to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Command</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            The average federal acquisition involves 47 handoffs between teams. 
            AcuSightPro reduces that to 3.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Chaos Side */}
          <div className="relative">
            <div className="absolute inset-0 bg-red-100 rounded-3xl transform rotate-1"></div>
            <div className="relative bg-white rounded-2xl p-8 border-2 border-red-200 shadow-lg">
              <h3 className="text-xl font-bold text-red-700 mb-6 flex items-center gap-2">
                <span className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600">✕</span>
                The Old Way
              </h3>
              
              <div className="space-y-4 mb-6">
                {chaosItems.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <item.icon className={`w-6 h-6 text-${item.color}-500`} />
                    <span className="text-gray-700">{item.label}</span>
                  </div>
                ))}
              </div>

              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <p className="text-red-800 text-sm italic">
                  "I spend more time hunting for information than actually doing the work."
                </p>
                <p className="text-red-600 text-xs mt-2">Every CO, ever</p>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="hidden lg:flex items-center justify-center absolute left-1/2 transform -translate-x-1/2">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
              <ArrowRight className="w-8 h-8 text-white" />
            </div>
          </div>

          {/* Order Side */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 rounded-3xl transform -rotate-1"></div>
            <div className="relative bg-white rounded-2xl p-8 border-2 border-purple-200 shadow-lg">
              <h3 className="text-xl font-bold text-purple-700 mb-6 flex items-center gap-2">
                <span className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white">
                  <Zap className="w-4 h-4" />
                </span>
                The AcuSightPro Way
              </h3>
              
              <div className="space-y-4 mb-6">
                {orderItems.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-emerald-500" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-4 text-white">
                <p className="text-sm font-medium">
                  One platform. Real-time collaboration. Zero handoff delays.
                </p>
                <p className="text-purple-200 text-xs mt-2">Work gets done, not passed around.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">
            <span className="font-bold text-gray-900">The result:</span> Federal contracting officers spend 62% of their time on admin tasks.
          </p>
          <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
            With ASP, it&apos;s 15%.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CollaborationChaos;
